import { RowSelectionState } from "@tanstack/react-table";
import isEmpty from "lodash/isEmpty";
import { FiltersV2StorePage } from "PFApp/components/filters_v2/stores/filters_v2_store/filters_v2_store.types";
import { Option } from "PFComponents/multi_toggle";
import { Activity, BasicProfile, Id } from "PFTypes";
import { RecentSearchesPageKey } from "PFTypes/recent_searches";
import { Dispatch, ReactNode, SetStateAction, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { MatchesTableEntry } from "./components/matches_panel/matches_table";
import { ShortlistWithScores } from "./hooks/use_shortlist_items";
import { OPTION_ORDER, ResourceTableOption } from "./resource_table_option";
import { ResourceTableToggleContent } from "./resource_table_toggle_content";

export type RowSelection = {
  onRowSelectionChange?: Dispatch<SetStateAction<RowSelectionState>>;
  rowSelectionState?: RowSelectionState;
};

export type ActionCellData = MatchesTableEntry | BasicProfile | ShortlistWithScores;

export type RenderActionCell = (row: { data: ActionCellData; option: ResourceTableOption }) => ReactNode;

export type ResourceTableMatchesProps = {
  activity: Activity;
  matchesFiltersStorePage: FiltersV2StorePage;
  excludedProfileIds?: number[];
  renderNoMatchesActionNamedResourceFallback?: (onNamedResourceClick: VoidFunction) => ReactNode;
};

export type ResourceTableShortlistsProps = {
  activity: Activity;
  shortlistsFiltersStorePage: FiltersV2StorePage;
  onShortlistUndo?: (shortlistId: Id) => void;
};

export type ResourceTableToggleProps = RowSelection & {
  pageKey: RecentSearchesPageKey;
  availableOptions?: ResourceTableOption[];
  selectedOption?: ResourceTableOption;
  defaultSelectedOption?: ResourceTableOption;
  onSelectedOptionChange?: (option: ResourceTableOption) => void;
  resetSelectionOnOptionChange?: boolean;
  matchesOptions?: ResourceTableMatchesProps;
  shortlistsOptions?: ResourceTableShortlistsProps;
  renderActionCell?: RenderActionCell;
};

export const ResourceTableToggle = ({
  availableOptions,
  selectedOption,
  defaultSelectedOption,
  onSelectedOptionChange,
  resetSelectionOnOptionChange,
  onRowSelectionChange,
  rowSelectionState,
  pageKey,
  matchesOptions,
  shortlistsOptions,
  renderActionCell
}: ResourceTableToggleProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "resourceTableToggle.options" });

  const visibleOptions: Option<ResourceTableOption>[] = useMemo(() => {
    const allOptions: Option<ResourceTableOption>[] = [
      ...(shortlistsOptions ? [{ id: ResourceTableOption.Shortlists, value: t("shortlists") }] : []),
      ...(matchesOptions ? [{ id: ResourceTableOption.Matches, value: t("matches") }] : []),
      { id: ResourceTableOption.NamedResource, value: t("namedResource") }
    ];

    if (isEmpty(availableOptions)) {
      return allOptions;
    }

    const allowed = new Set(availableOptions);
    return OPTION_ORDER.filter((option) => allowed.has(option)).map(
      (option) => allOptions.find(({ id }) => id === option)!
    );
  }, [availableOptions, matchesOptions, shortlistsOptions, t]);

  if (isEmpty(visibleOptions)) {
    return null;
  }

  return (
    <ResourceTableToggleContent
      visibleOptions={visibleOptions}
      selectedOption={selectedOption}
      defaultSelectedOption={defaultSelectedOption}
      onSelectedOptionChange={onSelectedOptionChange}
      resetSelectionOnOptionChange={resetSelectionOnOptionChange}
      onRowSelectionChange={onRowSelectionChange}
      rowSelectionState={rowSelectionState}
      pageKey={pageKey}
      matchesOptions={matchesOptions}
      shortlistsOptions={shortlistsOptions}
      renderActionCell={renderActionCell}
    />
  );
};
